import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyticsAPI, tenantAPI, dashboardAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useBranchStore } from '../../store';
import OnlineNowCard from './OnlineNowCard';
import BandwidthCard from './BandwidthCard';
import './Dashboard.css';

const SuperAdminDashboard = () => {
    // The tenant / branch picked in the header narrows the online card (none: every tenant).
    const selectedTenant = useBranchStore((state) => state.selectedTenant);
    const selectedBranch = useBranchStore((state) => state.selectedBranch);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [tenants, setTenants] = useState([]);
    const [systemStatus, setSystemStatus] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(new Date());
    const [currentTime, setCurrentTime] = useState(new Date());

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const fetchGlobalData = useCallback(async () => {
        try {
            setLoading(true);
            const [statsRes, tenantRes] = await Promise.all([
                analyticsAPI.getDashboard(null, 'today'),
                tenantAPI.getAll(),
            ]);
            setStats(statsRes.data.data);
            setTenants(tenantRes.data.data.tenants || []);
            setLastUpdated(new Date());
        } catch (error) {
            toast.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }

        // Separate, non-fatal — the System Status panel shouldn't block the
        // rest of the dashboard if this one lightweight check has an issue.
        try {
            const res = await dashboardAPI.getSystemStatus();
            setSystemStatus(res.data.data);
        } catch { /* panel just shows nothing extra if this fails */ }
    }, []);

    useEffect(() => { fetchGlobalData(); }, [fetchGlobalData]);

    const { overview, revenue, systemHealth } = stats || {};

    const greeting = () => {
        const h = new Date().getHours();
        if (h < 12) return 'Good Morning';
        if (h < 17) return 'Good Afternoon';
        return 'Good Evening';
    };

    const fmtTime = (d) => d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const fmtDate = (d) => d.toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

    const statCards = [
        {
            label: 'Total Tenants',
            value: overview?.totalTenants ?? tenants.length,
            icon: 'fa-building',
            color: 'blue',
            footer: `+${overview?.newTenants || 0} new this month`,
            footerIcon: 'fa-arrow-up',
            footerColor: 'success',
            onClick: () => navigate('/tenants'),
        },
        {
            label: 'Total Revenue',
            value: `₹${(revenue?.total || 0).toLocaleString('en-IN')}`,
            icon: 'fa-indian-rupee-sign',
            color: 'green',
            footer: `${revenue?.growth >= 0 ? '+' : ''}${revenue?.growth || 0}% vs last month`,
            footerIcon: revenue?.growth >= 0 ? 'fa-arrow-up' : 'fa-arrow-down',
            footerColor: revenue?.growth >= 0 ? 'success' : 'danger',
        },
        {
            label: 'Active Sessions',
            value: (overview?.activeSessions || 0).toLocaleString(),
            icon: 'fa-wifi',
            color: 'purple',
            footer: 'Live connections',
            footerIcon: 'fa-circle',
            footerColor: 'live',
        },
        {
            label: 'Total Users',
            value: (overview?.totalUsers || 0).toLocaleString(),
            icon: 'fa-users',
            color: 'cyan',
            footer: 'Across all branches',
            footerIcon: 'fa-globe',
            footerColor: 'neutral',
        },
        {
            label: 'Total Branches',
            value: overview?.totalBranches || 0,
            icon: 'fa-code-branch',
            color: 'orange',
            footer: 'Active locations',
            footerIcon: 'fa-map-marker-alt',
            footerColor: 'neutral',
            onClick: () => navigate('/branches'),
        },
        {
            label: 'System Health',
            value: `${systemHealth?.score || 98}%`,
            icon: 'fa-heartbeat',
            color: 'pink',
            footer: 'All systems operational',
            footerIcon: 'fa-check-circle',
            footerColor: 'success',
        },
    ];

    // Reflects the real check from GET /api/dashboard/system-status — this
    // used to be a hardcoded "everything is operational" array that never
    // actually checked anything, so it kept claiming Redis/RADIUS/MongoDB
    // were fine even when they weren't. `systemStatus` is null until that
    // call resolves, in which case each row shows "Checking…" rather than a
    // fabricated status.
    const isUp = (key) => systemStatus?.[key]?.status === 'operational' || systemStatus?.[key]?.status === 'reachable';
    const systemServices = [
        { name: 'API Server', icon: 'fa-server', status: systemStatus ? 'operational' : 'checking', statusText: systemStatus ? 'Operational' : 'Checking…' },
        { name: 'MongoDB', icon: 'fa-database', status: !systemStatus ? 'checking' : (isUp('mongodb') ? 'operational' : 'down'), statusText: !systemStatus ? 'Checking…' : (isUp('mongodb') ? 'Connected' : 'Disconnected') },
        { name: 'Redis Cache', icon: 'fa-bolt', status: !systemStatus ? 'checking' : (isUp('redis') ? 'operational' : 'down'), statusText: !systemStatus ? 'Checking…' : (isUp('redis') ? `Active (${systemStatus.redis.mode})` : 'Down') },
        { name: 'RADIUS Server', icon: 'fa-shield-alt', status: !systemStatus ? 'checking' : (isUp('radius') ? 'operational' : 'down'), statusText: !systemStatus ? 'Checking…' : (isUp('radius') ? 'Reachable' : 'Unreachable') },
    ];
    const allOperational = systemServices.every(s => s.status === 'operational');

    const quickActions = [
        { label: 'New Tenant', icon: 'fa-plus-circle', color: 'blue', path: '/tenants' },
        { label: 'Add NAS', icon: 'fa-server', color: 'purple', path: '/nas' },
        { label: 'Internet Plans', icon: 'fa-wifi', color: 'green', path: '/plans' },
        { label: 'Vouchers', icon: 'fa-ticket-alt', color: 'orange', path: '/vouchers' },
        { label: 'Analytics', icon: 'fa-chart-line', color: 'cyan', path: '/analytics' },
        { label: 'Administrators', icon: 'fa-user-shield', color: 'pink', path: '/admins' },
    ];

    const typeConfig = {
        hotel:      { bg: '#fef3c7', color: '#92400e', icon: 'fa-hotel' },
        isp:        { bg: '#dbeafe', color: '#1e40af', icon: 'fa-wifi' },
        mall:       { bg: '#fce7f3', color: '#9d174d', icon: 'fa-store' },
        campus:     { bg: '#d1fae5', color: '#065f46', icon: 'fa-graduation-cap' },
        enterprise: { bg: '#ede9fe', color: '#5b21b6', icon: 'fa-building' },
        other:      { bg: '#f3f4f6', color: '#374151', icon: 'fa-globe' },
    };

    if (loading) return (
        <div className="sad-loading">
            <div className="sad-spinner"></div>
            <p>Loading dashboard...</p>
        </div>
    );

    return (
        <div className="sad-wrapper">

            {/* ── Header ───────────────────────────────── */}
            <div className="sad-header">
                <div className="sad-header-left">
                    <div className="sad-greeting">
                        <span className="sad-greeting-wave">👋</span>
                        <div>
                            <h1>{greeting()}, Super Admin</h1>
                            <p>{fmtDate(currentTime)}</p>
                        </div>
                    </div>
                </div>
                <div className="sad-header-right">
                    <div className="sad-live-time">
                        <i className="fas fa-clock"></i>
                        <span>{fmtTime(currentTime)}</span>
                    </div>
                    <div className="sad-last-updated">
                        Updated {lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    <button className="sad-refresh-btn" onClick={fetchGlobalData}>
                        <i className="fas fa-sync-alt"></i>
                        Refresh
                    </button>
                </div>
            </div>

            {/* ── 6 Stat Cards ─────────────────────────── */}
            <div className="sad-stats-grid">
                {statCards.map((card, i) => (
                    <div
                        key={i}
                        className={`sad-stat-card sad-stat-${card.color} ${card.onClick ? 'sad-clickable' : ''}`}
                        onClick={card.onClick}
                    >
                        <div className="sad-stat-top">
                            <div className={`sad-stat-icon sad-icon-${card.color}`}>
                                <i className={`fas ${card.icon}`}></i>
                            </div>
                            <span className="sad-stat-label">{card.label}</span>
                        </div>
                        <div className="sad-stat-value">{card.value}</div>
                        <div className={`sad-stat-footer sad-footer-${card.footerColor}`}>
                            <i className={`fas ${card.footerIcon}`}></i>
                            <span>{card.footer}</span>
                        </div>
                    </div>
                ))}
            </div>

            {/* ── Main Content Grid ─────────────────────── */}
            <div className="sad-content-grid">

                {/* Left — Tenants Table */}
                <div className="sad-col-main">
                    <div className="sad-card">
                        <div className="sad-card-header">
                            <div className="sad-card-title">
                                <i className="fas fa-building"></i>
                                <h2>Recent Organizations</h2>
                                <span className="sad-badge">{tenants.length}</span>
                            </div>
                            <button className="sad-view-all" onClick={() => navigate('/tenants')}>
                                View All <i className="fas fa-arrow-right"></i>
                            </button>
                        </div>
                        <div className="sad-table-wrap">
                            <table className="sad-table">
                                <thead>
                                    <tr>
                                        <th>Organization</th>
                                        <th>Type</th>
                                        <th>Plan</th>
                                        <th>Status</th>
                                        <th>Joined</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tenants.slice(0, 8).map((t) => {
                                        const tc = typeConfig[t.type] || typeConfig.other;
                                        return (
                                            <tr key={t._id}>
                                                <td>
                                                    <div className="sad-org-cell">
                                                        <div className="sad-org-avatar" style={{ background: tc.bg, color: tc.color }}>
                                                            {t.name.charAt(0).toUpperCase()}
                                                        </div>
                                                        <div>
                                                            <div className="sad-org-name">{t.name}</div>
                                                            <div className="sad-org-slug">{t.slug}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="sad-type-badge" style={{ background: tc.bg, color: tc.color }}>
                                                        <i className={`fas ${tc.icon}`}></i>
                                                        {t.type || 'enterprise'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="sad-plan-badge">
                                                        {t.subscription?.plan || 'Basic'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`sad-status-badge ${t.status === 'active' ? 'sad-status-active' : 'sad-status-inactive'}`}>
                                                        <span className="sad-status-dot"></span>
                                                        {t.status}
                                                    </span>
                                                </td>
                                                <td className="sad-date-cell">
                                                    {new Date(t.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                </td>
                                                <td>
                                                    <button className="sad-action-btn" onClick={() => navigate(`/tenants`)}>
                                                        <i className="fas fa-eye"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {tenants.length === 0 && (
                                        <tr>
                                            <td colSpan={6} className="sad-empty-row">
                                                <i className="fas fa-building"></i>
                                                <span>No organizations yet</span>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Tenant Type Distribution */}
                    <div className="sad-card sad-distribution-card">
                        <div className="sad-card-header">
                            <div className="sad-card-title">
                                <i className="fas fa-chart-pie"></i>
                                <h2>Tenant Distribution</h2>
                            </div>
                        </div>
                        <div className="sad-distribution-grid">
                            {Object.entries(
                                tenants.reduce((acc, t) => {
                                    acc[t.type || 'other'] = (acc[t.type || 'other'] || 0) + 1;
                                    return acc;
                                }, {})
                            ).map(([type, count]) => {
                                const tc = typeConfig[type] || typeConfig.other;
                                const pct = tenants.length ? Math.round((count / tenants.length) * 100) : 0;
                                return (
                                    <div key={type} className="sad-dist-item">
                                        <div className="sad-dist-header">
                                            <span className="sad-dist-icon" style={{ background: tc.bg, color: tc.color }}>
                                                <i className={`fas ${tc.icon}`}></i>
                                            </span>
                                            <span className="sad-dist-label">{type}</span>
                                            <span className="sad-dist-count">{count}</span>
                                        </div>
                                        <div className="sad-dist-bar-track">
                                            <div className="sad-dist-bar-fill" style={{ width: `${pct}%`, background: tc.color }}></div>
                                        </div>
                                        <span className="sad-dist-pct">{pct}%</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Right — Status + Actions */}
                <div className="sad-col-side">

                    {/* System Status */}
                    <div className="sad-card">
                        <div className="sad-card-header">
                            <div className="sad-card-title">
                                <i className="fas fa-heartbeat"></i>
                                <h2>System Status</h2>
                            </div>
                            <span className={`sad-all-good ${allOperational ? '' : 'sad-svc-down'}`}>
                                <span className="sad-pulse-dot"></span>
                                {!systemStatus ? 'Checking…' : allOperational ? 'All Operational' : 'Issue Detected'}
                            </span>
                        </div>
                        <div className="sad-services-list">
                            {systemServices.map((service, i) => (
                                <div key={i} className="sad-service-row">
                                    <div className="sad-service-left">
                                        <div className="sad-service-icon">
                                            <i className={`fas ${service.icon}`}></i>
                                        </div>
                                        <span className="sad-service-name">{service.name}</span>
                                    </div>
                                    <div className="sad-service-right">
                                        <span className={`sad-service-status sad-svc-${service.status}`}>
                                            <span className="sad-status-dot"></span>
                                            {service.statusText}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="sad-uptime-bar">
                            <div className="sad-uptime-label">
                                <span>Uptime</span>
                                <span className="sad-uptime-pct">99.9%</span>
                            </div>
                            <div className="sad-uptime-track">
                                <div className="sad-uptime-fill"></div>
                            </div>
                        </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="sad-card">
                        <div className="sad-card-header">
                            <div className="sad-card-title">
                                <i className="fas fa-bolt"></i>
                                <h2>Quick Actions</h2>
                            </div>
                        </div>
                        <div className="sad-actions-grid">
                            {quickActions.map((action, i) => (
                                <button
                                    key={i}
                                    className={`sad-action-card sad-action-${action.color}`}
                                    onClick={() => navigate(action.path)}
                                >
                                    <i className={`fas ${action.icon}`}></i>
                                    <span>{action.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Platform Summary */}
                    <div className="sad-card sad-summary-card">
                        <div className="sad-card-header">
                            <div className="sad-card-title">
                                <i className="fas fa-chart-bar"></i>
                                <h2>Platform Summary</h2>
                            </div>
                        </div>
                        <div className="sad-summary-list">
                            {[
                                { label: 'Active Tenants', value: tenants.filter(t => t.status === 'active').length, icon: 'fa-check-circle', color: '#16a34a' },
                                { label: 'Inactive Tenants', value: tenants.filter(t => t.status !== 'active').length, icon: 'fa-times-circle', color: '#dc2626' },
                                { label: 'Hotel Properties', value: tenants.filter(t => t.type === 'hotel').length, icon: 'fa-hotel', color: '#d97706' },
                                { label: 'ISP Networks', value: tenants.filter(t => t.type === 'isp').length, icon: 'fa-wifi', color: '#2563eb' },
                                { label: 'Enterprise', value: tenants.filter(t => t.type === 'enterprise').length, icon: 'fa-building', color: '#7c3aed' },
                            ].map((item, i) => (
                                <div key={i} className="sad-summary-row">
                                    <div className="sad-summary-left">
                                        <i className={`fas ${item.icon}`} style={{ color: item.color }}></i>
                                        <span>{item.label}</span>
                                    </div>
                                    <span className="sad-summary-value">{item.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                </div>
            </div>

            {/* ── Online now, gateway by gateway (MikroTik live, Ruijie and others from accounting) ── */}
            <OnlineNowCard tenantId={selectedTenant?._id} branchId={selectedBranch?._id} />
            <BandwidthCard tenantId={selectedTenant?._id} branchId={selectedBranch?._id} />
        </div>
    );
};

export default SuperAdminDashboard;
